import React, { useState, useMemo, useRef, useContext } from 'react';
import moment from 'moment';
import { useIntl } from 'umi';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import BarChart from '@/pages/dashboard/components/Chart/components/BarChart';
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart';
import CardBox from '@/pages/dashboard/components/CardBox';
import { unitConversion, formatSmallValue, formatPercentNum, commonValueFormatter } from '@/pages/dashboard/utils/utils';
import { formatDataByChartType, addChartLabel, xAxisHighLight, changeMin } from '@/pages/dashboard/components/Chart/_config/plug';
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { getDecimalFormateStr } from '@/pages/dashboard/components/MetricCard/utils';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { dynamicString } from '@home/utils/dynamicString';

interface ISubTitleItem {
  titleEn: string;
  titleCn: string;
  subTitle1En: string;
  subTitle1Cn: string;
  subTitle2En: string;
  subTitle2Cn: string;
  subTitle3En: string;
  subTitle3Cn: string;
}

interface IMetricValue {
  value: number | string;
}

interface IMetricsListItem {
  date: string;
  metrics: Record<string, IMetricValue>;
}

interface ISummaryItem {
  name: string;
  avg: string;
  sum: string;
  max: string;
}

export const tabNames: Record<string, string> = {
  Day: 'daily',
  Week: 'weekly',
  Month: 'monthly'
};

const DateComparisonMetricsTrend = (props: any) => {
  const {
    config,
    selectedTimeSegment,
    selectedSegment,
    style,
  } = props;
  const { metrics, dataFrame, metricsInfo } = props?.data || {};
  const { subTitleCom, dataKey = 'keyMetricsData' } = config || {};

  // 构建用于 dynamicString 的数据源
  const dynamicData = useMemo(() => ({
    ...(props?.data || {}),
    metricsInfo,
  }), [props?.data, metricsInfo]);

  const selectedItem = dataFrame === 'pc_inflextion' ? metrics : selectedTimeSegment?.find((item: { value: string; }) => item.value === selectedSegment);
  const { enName: rawEnName, cnName: rawCnName, metric_key, type, decimal, formatter: rawFormatter } = selectedItem || {};

  // 支持 dynamicString 模板解析 enName/cnName/formatter
  const resolvedEnName = rawEnName ? dynamicString(rawEnName, dynamicData, rawEnName) : '';
  const resolvedCnName = rawCnName ? dynamicString(rawCnName, dynamicData, rawCnName) : '';
  const resolvedFormatter = rawFormatter ? dynamicString(rawFormatter, dynamicData, '') : '';
  const enName = resolvedEnName;
  const cnName = resolvedCnName;

  const { loading, data, isError } = useMemo(() => {
    const apiData = props?.data?.[dataKey];
    if (apiData) {
      return {
        loading: apiData.loading,
        data: apiData.data,
        isError: apiData.errorMsg != ''
      };
    }
    return {
      loading: false,
      data: {},
      isError: true
    };
  }, [props?.data]);

  const { statisticSumData, statisticAvgData, statisticMaxData, contrastSumData, contrastAvgData, contrastMaxData, statistics_metrics_list, contrast_metrics_list } = useMemo(() => {
    // 通用的帮助函数，按类型获取指定数据
    const getMetricsByType = (arr: any[], type: string) => arr?.find(item => item.type === type)?.metrics[metric_key] || {};

    const statisticSumData = getMetricsByType(data?.statistics_avg_sum_metrics, 'sum');
    const statisticAvgData = getMetricsByType(data?.statistics_avg_sum_metrics, 'avg');
    const statisticMaxData = getMetricsByType(data?.statistics_avg_sum_metrics, 'max');

    const contrastSumData = getMetricsByType(data?.contrast_avg_sum_metrics, 'sum');
    const contrastAvgData = getMetricsByType(data?.contrast_avg_sum_metrics, 'avg');
    const contrastMaxData = getMetricsByType(data?.contrast_avg_sum_metrics, 'max');

    const statistics_metrics_list = data?.statistics_metrics_list || [];
    const contrast_metrics_list = data?.contrast_metrics_list || [];

    return { statisticSumData, statisticAvgData, statisticMaxData, contrastSumData, contrastAvgData, contrastMaxData, statistics_metrics_list, contrast_metrics_list };
  }, [data, metric_key]);

  const { filterVal } = useContext(FilterContext);
  const { statisticDate_timeType: timeType } = filterVal;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lineChartRef = useRef<any>(null);
  const lineChartObj = lineChartRef?.current?.instance();
  const { isEN, getLocalText } = useLocal();
  const { formatMessage } = useIntl(); // 中英切换方法
  const [iconType, setIconType] = useState('lineChart');
  const [iseye, setIseye] = useState(true);
  const [showChangeTag, setShowChangeTag] = useState(false);
  // const [isError, setIsError] = useState(false)
  const getVal = (value: string | number) => {
    if (resolvedFormatter) {
      return dataFormat({ value, formatRule: resolvedFormatter });
    }
    return metric_key === 'arpu' && Number(value) < 1 && Number(value) > 0 ? formatSmallValue(value, 'decimals', decimal) : commonValueFormatter(type, value, decimal);
  };

  // 趋势图标题
  const summaryData = useMemo(() => {
    const avgData = [statisticAvgData, contrastAvgData,];
    const sumData = [statisticSumData, contrastSumData,];
    const maxData = [statisticMaxData, contrastMaxData,];

    const summaryData = subTitleCom?.map((item: ISubTitleItem, index: number) => ({
      name: isEN ? item.titleEn : item.titleCn,
      avg: `${isEN ? item.subTitle1En : item.subTitle1Cn} : ${getDecimalFormateStr(
        type,
        avgData[index]?.value,
        metric_key,
        decimal
      ) || '-'}`,
      sum: `${isEN ? item.subTitle2En : item.subTitle2Cn} : ${getDecimalFormateStr(
        type,
        sumData[index]?.value,
        metric_key,
        decimal
      ) || '-'}`,
      max: `${isEN ? item.subTitle3En : item.subTitle3Cn} : ${getDecimalFormateStr(
        type,
        maxData[index]?.value,
        metric_key,
        decimal
      ) || '-'}`,
    }));

    return summaryData;
  }, [isEN, statisticAvgData, statisticSumData,statisticMaxData, contrastAvgData, contrastSumData,contrastMaxData]);

  // qushit
  const { chart: chartOption, xAxis1, xAxis2 } = useMemo(() => {
    const xAxis1: string[] = [];
    const xAxis2: string[] = [];
    const chartOption = (() => {
      const hasData = (() => statistics_metrics_list?.some((item: IMetricsListItem) => item.metrics?.[metric_key]) || contrast_metrics_list?.some((item: IMetricsListItem) => item.metrics?.[metric_key]))();

      if (!hasData) {
        return [
          {
            name: isEN ? enName : cnName,
            data: []
          },
          {
            name: `${getLocalText('common_contrast')}${isEN ? ' ' : ''}${isEN ? enName : cnName}`,
            data: []
          },
        ];
      }
      let isNoData = true;
      const statisticsData = statistics_metrics_list?.map((item: any) => {
        const x = moment(item.date).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD') || '';
        xAxis1.push(x);
        if (item.metrics?.[metric_key]?.value) isNoData = false;
        return {
          x,
          y: item.metrics?.[metric_key]?.value || ''
          // y: getDecimalFormateStr(type, item.metrics?.[metric_key]?.value, metric_key, decimal) || ''


        };
      });
      const contrastData = contrast_metrics_list?.map((item: any) => {
        const x = moment(item.date).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD') || '';
        xAxis2.push(x);
        if (item.metrics?.[metric_key]?.value) isNoData = false;
        return {
          x,
          y: item.metrics?.[metric_key]?.value || ''
          // y: getDecimalFormateStr(type, item.metrics?.[metric_key]?.value, metric_key, decimal) || ''

        };
      });
      return [
        {
          name: isEN ? enName : cnName,
          data: isNoData ? [] : statisticsData
        },
        {
          name: `${getLocalText('common_contrast')}${isEN ? ' ' : ''}${isEN ? enName : cnName}`,
          data: isNoData ? [] : contrastData
        },
      ];
    })();
    return {
      chart: chartOption,
      table: {} as any,
      xAxis1,
      xAxis2
    };
  }, [statistics_metrics_list, contrast_metrics_list, enName, cnName, metric_key, isEN]);

  const { isNoData, barOptions, lineOptions } = useMemo(() => {
    const isNoData = chartOption?.every(item => item.data?.length === 0);
    const barOptions = formatDataByChartType('bar', chartOption, {}, true);
    const lineOptions = formatDataByChartType('line', chartOption, {}, true);
    return { isNoData, barOptions, lineOptions };
  }, [chartOption]);

  const handleAxisFormat = (value: any) => timeType === "Month" ? moment(value).format('YYYY-MM') : moment(value).format('MM-DD');

  const formatterFunc = (params: any) => {
    const xAxis = [xAxis1, xAxis2];
    const splitData = params?.map((item: any, index: any) => {
      const metricsNum = getDecimalFormateStr(type, item?.value, metric_key, decimal);

      return {
        title: moment(xAxis[index]?.[item?.dataIndex]).format(timeType === 'Month' ? 'YYYY-MM' : 'YYYY-MM-DD'),
        data: [
          {
            color: params[index].color,
            name: params[index].seriesName,
            metricsNum
          }
        ]
      };
    });
    return getEchartTooltipText({
      isSplit: true,
      splitData
    });
  };

  const yAxisLabelFormat = (value: any) => {
    const formatLabel = value ? unitConversion(value, true) : value;
    const num = value < 1 && value > 0 ? parseFloat(value.toFixed(3)) : formatLabel;
    return num;
  };

  const setOtherConfigFunc = (opt: any, timeType: any, isLine = false) => {
    // opt.series = testMinValue(opt,0.00001);
    opt.xAxis = [
      {
        data: xAxis1,
        type: 'category',
        offset: 5,
        axisTick: {
          alignWithLabel: true
        },
        axisLabel: {
          color: '#838697',
          fontSize: 10,
          formatter: (value: string) => moment(value).isValid() && timeType !== 'Month' ? moment(value).format('MM-DD') : value
        }
      },
      {
        data: xAxis2,
        type: 'category',
        position: 'bottom',
        offset: 20,
        axisTick: {
          show: false,
          axisTick: {
            alignWithLabel: true
          },
        },
        axisLine: { show: false },
        axisLabel: {
          color: '#838697',
          fontSize: 10,
          formatter: (value: string) => moment(value).isValid() && timeType !== 'Month' ? moment(value).format('MM-DD') : value
        }
      }
    ];

    if (isLine) {
      opt = changeMin(opt, lineChartObj, {
        show: showChangeTag,
        showFormat: (value: any) => {
          if (type !== 'percent') return yAxisLabelFormat(value);
          if (value > 0 && value < 0.1 && String(value).length > 3) return formatPercentNum(+value, 2);
          return formatPercentNum(value, 1);
        }
      });
    }

    opt = addChartLabel(opt, {
      formatter: (params: any) => getVal(params?.value),
      show: iseye,
      formatNum: (value: any) => getVal(value)
    });

    opt = xAxisHighLight(opt, {
      formatter: (params: { value: string; }) => handleAxisFormat(params?.value),
      isDateComparison: true
    });

    if (type === 'percent') {
      opt.yAxis.axisLabel.formatter = (value: number) => formatPercentNum(value, 2);
    }
    if (metric_key === 'arpu') {
      opt.yAxis.axisLabel.formatter = (value: number) => {
        if (value < 1 && value > 0) return formatSmallValue(value, 'decimals', 2);
        return yAxisLabelFormat(value);
      };
    }
    return opt;
  };

  const renderList = [
    {
      type: 'eyes',
      svgType: iseye ? 'open-eyes' : 'eyes-closed',
      content: iseye ? formatMessage({ id: 'common_show_values' }) : formatMessage({ id: 'common_hide_values' }),
      isHideCom: true,
    },
    iconType === 'lineChart' && {
      type: 'turn',
      svgType: 'left-turn-right',
      content: showChangeTag ? formatMessage({ id: 'change_tag_bubble_show' }) : formatMessage({ id: 'change_tag_bubble_hidden' }),
      isHideCom: true,
    },
    {
      type: 'barChart',
      svgType: 'column-chart',
      content: formatMessage({ id: 'common_bar_chart' }),
      component: <BarChart
        height={340}
        legendData={barOptions?.legend ?? []}
        xAxisData={barOptions?.xAxisData ?? []}
        series={barOptions?.series ?? []}
        formatterFunc={formatterFunc}
        setOtherConfigFunc={(opt: any) => setOtherConfigFunc(opt, timeType)}
      />
    },
    {
      type: 'lineChart',
      svgType: 'discount-chart',
      content: formatMessage({ id: 'common_line_chart' }),
      component: <LineChart
        height={340}
        ref={lineChartRef}
        legendData={lineOptions?.legend ?? []}
        xAxisData={lineOptions?.xAxisData ?? []}
        series={lineOptions?.series ?? []}
        formatterFunc={formatterFunc}
        setOtherConfigFunc={(opt: any) => setOtherConfigFunc(opt, timeType, true)}
      />
    },
  ]?.filter(v => v !== false);

  const subTitleCom1 = <div className="dateComparison-head-wrap">
    {
      summaryData?.map((item: ISummaryItem, index: number) => (
        <div className="dateComparison-head" key={item?.name || index}>
          <div className="dateComparison-head-title">
            <span>{item.name}</span>
          </div>
          <div className="dateComparison-head-num">
            <span>{item.avg}</span>
            <span>{item.sum}</span>
            <span>{item.max}</span>
          </div>
        </div>
      ))
    }
  </div>;

  return (
    <CardBox
      className='register-table'
      height={340}
      style={style}
      bordered={false}
      titleCom={isEN ? enName : cnName}
      subTitleCom={subTitleCom1}
      renderList={renderList}
      defaultAvtive={3}
      setCurrentDataType={(type: any) => {
        if (type === 'eyes') {
          setIseye(!iseye);
        }
        if (type === 'turn') {
          setShowChangeTag(!showChangeTag);
        }
        if (type !== 'eyes' && type !== 'turn') {
          setIconType(type);
        }
      }}
      isLoading={loading}
      isError={isError}
      isNodata={isNoData}
    />
  );
};

export default DateComparisonMetricsTrend;
